/**
 * Tanzania administrative geography (Phase 4.4c-0).
 *
 * Typed const tree of regions → districts → wards plus pure resolver
 * helpers used by both the backend onboarding validator and the
 * dashboard wizard's cascading region/district/ward selectors.
 *
 * The dataset shipped here is a deliberately small starter slice
 * (Dar es Salaam, Arusha, Mwanza, Dodoma) drawn from the PO-RALG
 * national administrative list. The full ~3,956-ward catalog will
 * land in a follow-up data-only update once the dashboard wizard is
 * wired up; the helper API does not change when the catalog grows.
 *
 * The data is intentionally a TS const, not a JSON load — the validator
 * cross-checks region/district/ward codes at request time and we don't
 * want to pay an I/O cost on every onboarding call. It also keeps the
 * helper pure so it can be imported from any runtime without a fs seam.
 */
/**
 * Codes follow the PO-RALG numeric scheme:
 *   region:   01–31
 *   district: <region>.<district> (e.g. 02.04)
 *   ward:     <region>.<district>.<ward> (e.g. 02.04.012)
 *
 * Names are kept in their canonical PO-RALG spelling.
 */
export const TANZANIA_REGIONS = [
    {
        code: '02',
        name: 'Dar es Salaam',
        districts: [
            {
                code: '02.01',
                name: 'Ilala',
                wards: [
                    { code: '02.01.001', name: 'Buguruni' },
                    { code: '02.01.002', name: 'Ilala' },
                    { code: '02.01.003', name: 'Kariakoo' },
                    { code: '02.01.004', name: 'Mchikichini' },
                    { code: '02.01.005', name: 'Upanga Mashariki' },
                ],
            },
            {
                code: '02.02',
                name: 'Kinondoni',
                wards: [
                    { code: '02.02.001', name: 'Kawe' },
                    { code: '02.02.002', name: 'Kinondoni' },
                    { code: '02.02.003', name: 'Magomeni' },
                    { code: '02.02.004', name: 'Mwananyamala' },
                    { code: '02.02.005', name: 'Sinza' },
                ],
            },
            {
                code: '02.03',
                name: 'Temeke',
                wards: [
                    { code: '02.03.001', name: 'Chang’ombe' },
                    { code: '02.03.002', name: 'Kurasini' },
                    { code: '02.03.003', name: 'Mbagala' },
                    { code: '02.03.004', name: 'Temeke' },
                ],
            },
            {
                code: '02.04',
                name: 'Ubungo',
                wards: [
                    { code: '02.04.001', name: 'Goba' },
                    { code: '02.04.002', name: 'Kibamba' },
                    { code: '02.04.003', name: 'Makuburi' },
                    { code: '02.04.004', name: 'Mbezi' },
                    { code: '02.04.005', name: 'Ubungo' },
                ],
            },
        ],
    },
    {
        code: '03',
        name: 'Arusha',
        districts: [
            {
                code: '03.01',
                name: 'Arusha City',
                wards: [
                    { code: '03.01.001', name: 'Daraja Mbili' },
                    { code: '03.01.002', name: 'Kaloleni' },
                    { code: '03.01.003', name: 'Kati' },
                    { code: '03.01.004', name: 'Sekei' },
                    { code: '03.01.005', name: 'Themi' },
                ],
            },
            {
                code: '03.02',
                name: 'Arusha District',
                wards: [
                    { code: '03.02.001', name: 'Bangata' },
                    { code: '03.02.002', name: 'Mateves' },
                    { code: '03.02.003', name: 'Olkokola' },
                ],
            },
        ],
    },
    {
        code: '07',
        name: 'Kilimanjaro',
        districts: [
            {
                code: '07.01',
                name: 'Moshi Urban',
                wards: [
                    { code: '07.01.001', name: 'Mji Mpya' },
                    { code: '07.01.002', name: 'Pasua' },
                    { code: '07.01.003', name: 'Kiusa' },
                    { code: '07.01.004', name: 'Majengo' },
                    { code: '07.01.005', name: 'Shirimatunda' },
                ],
            },
            {
                code: '07.02',
                name: 'Moshi Rural',
                wards: [
                    { code: '07.02.001', name: 'Mabogini' },
                    { code: '07.02.002', name: 'Old Moshi' },
                    { code: '07.02.003', name: 'Uru' },
                    { code: '07.02.004', name: 'Kibosho' },
                ],
            },
            {
                code: '07.03',
                name: 'Hai',
                wards: [
                    { code: '07.03.001', name: 'Hai Mjini' },
                    { code: '07.03.002', name: 'Bomang’ombe' },
                    { code: '07.03.003', name: 'Machame' },
                ],
            },
            {
                code: '07.04',
                name: 'Rombo',
                wards: [
                    { code: '07.04.001', name: 'Mkuu' },
                    { code: '07.04.002', name: 'Tarakea' },
                    { code: '07.04.003', name: 'Mengwe' },
                ],
            },
        ],
    },
    {
        code: '14',
        name: 'Mwanza',
        districts: [
            {
                code: '14.01',
                name: 'Nyamagana',
                wards: [
                    { code: '14.01.001', name: 'Buhongwa' },
                    { code: '14.01.002', name: 'Igoma' },
                    { code: '14.01.003', name: 'Mkolani' },
                    { code: '14.01.004', name: 'Nyamagana' },
                ],
            },
            {
                code: '14.02',
                name: 'Ilemela',
                wards: [
                    { code: '14.02.001', name: 'Ilemela' },
                    { code: '14.02.002', name: 'Kirumba' },
                    { code: '14.02.003', name: 'Pasiansi' },
                ],
            },
        ],
    },
    {
        code: '06',
        name: 'Dodoma',
        districts: [
            {
                code: '06.01',
                name: 'Dodoma City',
                wards: [
                    { code: '06.01.001', name: 'Chamwino' },
                    { code: '06.01.002', name: 'Dodoma Makulu' },
                    { code: '06.01.003', name: 'Hazina' },
                    { code: '06.01.004', name: 'Kikuyu Kaskazini' },
                ],
            },
        ],
    },
];
const REGION_INDEX = new Map(TANZANIA_REGIONS.map((r) => [r.code, r]));
export function listRegions() {
    return TANZANIA_REGIONS;
}
export function findRegion(regionCode) {
    return REGION_INDEX.get(regionCode) ?? null;
}
export function listDistricts(regionCode) {
    return findRegion(regionCode)?.districts ?? [];
}
export function findDistrict(regionCode, districtCode) {
    const region = findRegion(regionCode);
    if (!region)
        return null;
    return region.districts.find((d) => d.code === districtCode) ?? null;
}
export function listWards(regionCode, districtCode) {
    return findDistrict(regionCode, districtCode)?.wards ?? [];
}
export function findWard(regionCode, districtCode, wardCode) {
    const district = findDistrict(regionCode, districtCode);
    if (!district)
        return null;
    return district.wards.find((w) => w.code === wardCode) ?? null;
}
/**
 * Resolve the full triple in one call. Returns null if any leg is
 * missing or any code does not belong under its parent. The validator
 * uses this as the cross-check on TZ payloads, so a drift between the
 * dashboard cascade and the server catalog surfaces as a 400 before any
 * write hits the database.
 */
export function resolveTzAddress(input) {
    const region = findRegion(input.regionCode);
    if (!region)
        return null;
    const district = region.districts.find((d) => d.code === input.districtCode);
    if (!district)
        return null;
    const ward = district.wards.find((w) => w.code === input.wardCode);
    if (!ward)
        return null;
    return { region, district, ward };
}
//# sourceMappingURL=tanzania.js.map